<script setup>
import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'

// Discreet "get the app" badges for the footer, present on every page. Reuses the
// same store logos as AppDownloadBanner; styled for the dark footer. Store URLs
// come from the server, so it hides until the app links exist.
const page = usePage()
const appStoreUrl = computed(() => page.props.appDownload?.appStore || '')
const playStoreUrl = computed(() => page.props.appDownload?.playStore || '')
const hasAnyStore = computed(() => !!appStoreUrl.value || !!playStoreUrl.value)
</script>

<template>
  <div v-if="hasAnyStore" dusk="footer-app-download" class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
    <span class="text-sm font-medium text-gray-300">Get the app</span>
    <div class="flex flex-wrap gap-3">
      <a
        v-if="appStoreUrl"
        :href="appStoreUrl"
        target="_blank"
        rel="noopener"
        dusk="footer-app-store-link"
        aria-label="Download on the App Store"
        class="flex items-center gap-2.5 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
      >
        <svg class="size-6 flex-none" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.5 43-17.8 75.8-17.8 31.8 0 48.3 17.8 76.4 17.8 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
        </svg>
        <span>
          <span class="block text-[10px] leading-none text-gray-400">Download on the</span>
          <span class="block text-sm font-semibold leading-tight">App Store</span>
        </span>
      </a>
      <a
        v-if="playStoreUrl"
        :href="playStoreUrl"
        target="_blank"
        rel="noopener"
        dusk="footer-play-store-link"
        aria-label="Get it on Google Play"
        class="flex items-center gap-2.5 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
      >
        <svg class="size-5 flex-none" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#00D2FF" d="M3.6 2.2 13.3 12 3.6 21.8a1.7 1.7 0 0 1-.6-1.3V3.5c0-.5.2-1 .6-1.3Z" />
          <path fill="#00E676" d="M3.6 2.2c.4-.2.9-.2 1.4.1l11.7 6.6-2.9 2.9L3.6 2.2Z" />
          <path fill="#FFCE00" d="M16.7 8.9 20 10.8c.9.5.9 1.9 0 2.4l-3.3 1.9-3-3 3-3.2Z" />
          <path fill="#FF3D47" d="m3.6 21.8 10.2-9.9 2.9 2.9L5 21.7c-.5.3-1 .3-1.4.1Z" />
        </svg>
        <span>
          <span class="block text-[10px] leading-none text-gray-400">Get it on</span>
          <span class="block text-sm font-semibold leading-tight">Google Play</span>
        </span>
      </a>
    </div>
  </div>
</template>
